import type { FC, ReactNode } from 'react'
import type { StreamItem } from '@/types'

import { useEffect, useState } from 'react'
import { X, Youtube } from 'lucide-react'
import { cn } from '@/lib/utils'
import MediaImage from '@/components/landing/media-image'
import Sparkle from '@/components/landing/decor/sparkle'

interface LiveWidgetProps {
    initial: StreamItem | null
}

const POLL_MS = 90 * 1000

const HEADLINE: Record<StreamItem['status'], { full: string, short: string }> = {
    live: { full: "ON AIR", short: "ON AIR" },
    upcoming: { full: "NEXT STREAM", short: "NEXT" },
    archive: { full: "LATEST VIDEO", short: "NEW" },
    latest: { full: "LATEST VIDEO", short: "NEW" }
}

// Small card fixed under the top-right navigation that always shows the channel's state: the live
// stream while on air, otherwise the next waiting room, otherwise the newest video. It refreshes
// itself every 90 seconds and when the tab comes back into view.
const LiveWidget: FC<LiveWidgetProps> = ({ initial }): ReactNode => {
    const [item, setItem] = useState<StreamItem | null>(initial)
    const [ready, setReady] = useState(false)
    const [collapsed, setCollapsed] = useState(false)
    const [overFooter, setOverFooter] = useState(false)

    useEffect(() => {
        setItem(initial)
    }, [initial])

    useEffect(() => {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        const id = window.setTimeout(() => setReady(true), reduce ? 0 : 5200)

        return () => window.clearTimeout(id)
    }, [])

    useEffect(() => {
        let active = true

        const refresh = async (): Promise<void> => {
            if (document.visibilityState !== 'visible') return

            try {
                const response = await fetch('/api/streams/now', { headers: { Accept: 'application/json' } })

                if (!response.ok) return

                const data = await response.json() as { spotlight?: StreamItem | null }

                if (active && 'spotlight' in data) setItem(data.spotlight ?? null)
            }
            catch {
                // Offline or a temporary failure: keep showing the last known state.
            }
        }

        const onVisible = (): void => {
            if (document.visibilityState === 'visible') void refresh()
        }

        const first = window.setTimeout(refresh, 6000)
        const interval = window.setInterval(refresh, POLL_MS)

        document.addEventListener('visibilitychange', onVisible)

        return () => {
            active = false
            window.clearTimeout(first)
            window.clearInterval(interval)
            document.removeEventListener('visibilitychange', onVisible)
        }
    }, [])

    useEffect(() => {
        const footer = document.querySelector('footer')

        if (!footer || !('IntersectionObserver' in window)) return

        const observer = new IntersectionObserver(([entry]) => setOverFooter(entry.isIntersecting))

        observer.observe(footer)

        return () => observer.disconnect()
    }, [])

    if (!item) return null

    const live = item.status === 'live'
    const headline = HEADLINE[item.status]
    const visible = ready && !overFooter

    const card = (
        <>
            <span className='relative size-8 shrink-0 overflow-hidden rounded-full bg-ink/10 lg:aspect-video lg:h-auto lg:w-[124px] lg:rounded-lg'>
                {item.thumbnail
                    ? <MediaImage src={item.thumbnail} alt='' sizes='124px' className='object-cover' />
                    : <Sparkle className='absolute inset-0 m-auto size-4 text-gold' />}
                {live && <span className='absolute inset-0 rounded-full ring-2 ring-coral lg:rounded-lg' />}
            </span>

            <span className='flex min-w-0 flex-col gap-1'>
                <span className='flex items-center gap-1.5 font-mono text-[9px] tracking-[0.2em] lg:text-[10px] lg:tracking-[0.22em]'>
                    {live
                        ? (
                            <span className='inline-flex items-center gap-1 rounded-full bg-coral px-2 py-0.5 font-medium text-white'>
                                <Sparkle className='size-2.5 text-[#ffe9a8] motion-safe:animate-glint' />
                                <span className='motion-safe:animate-onair'>{headline.full}</span>
                            </span>
                        )
                        : (
                            <span className='text-ink'>
                                <span className='lg:hidden'>{headline.short}</span>
                                <span className='hidden lg:inline'>{headline.full}</span>
                            </span>
                        )}
                    <Youtube className='hidden size-3.5 text-ink-soft lg:block' />
                </span>
                <span
                    data-mdlfy-cms={item.source === 'cms' ? `streams:${item.id}:title` : undefined}
                    className='hidden text-[13px] leading-[1.6] tracking-[0.03em] text-ink lg:line-clamp-2'
                >
                    {item.title}
                </span>
                {item.dateLabel && (
                    <time dateTime={item.dateTime ?? undefined} className='hidden font-mono text-[9px] tracking-[0.14em] text-ink-soft lg:block'>
                        {item.dateLabel}
                    </time>
                )}
            </span>
        </>
    )

    const cardClass = 'flex items-center gap-2 rounded-full bg-mist/92 p-1 pr-3 text-left shadow-[0_18px_40px_-18px_rgba(29,42,51,0.5)] ring-1 ring-ink/10 backdrop-blur-md transition-transform duration-500 ease-[var(--ease-cinema)] hover:translate-y-0.5 lg:w-[330px] lg:gap-3 lg:rounded-2xl lg:p-2.5 lg:pr-4'

    // Fixed just below the header on the right, lined up with the end of the navigation, so it
    // never sits on the section texts. Phones get the small pill.
    return (
        <aside
            aria-label='YouTube の配信状況'
            className={cn(
                'fixed right-4 top-[4.5rem] z-40 transition-[opacity,translate] duration-700 ease-[var(--ease-cinema)] sm:right-5 lg:right-10 lg:top-[5.25rem]',
                visible ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-3 opacity-0'
            )}
        >
            {collapsed
                ? (
                    <button
                        type='button'
                        onClick={() => setCollapsed(false)}
                        aria-label='配信カードを開く'
                        className='relative flex size-10 items-center justify-center rounded-full bg-mist/92 lg:size-12 text-ink shadow-[0_14px_30px_-14px_rgba(29,42,51,0.5)] ring-1 ring-ink/10 backdrop-blur-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold'
                    >
                        <Youtube className='size-5' />
                        {live && <span className='absolute right-1.5 top-1.5 size-2.5 rounded-full bg-coral motion-safe:animate-onair' />}
                    </button>
                )
                : (
                    <div className='relative'>
                        {item.url
                            ? (
                                <a
                                    href={item.url}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={cn(cardClass, 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold')}
                                >
                                    {card}
                                    <span className='sr-only'>{`${item.title}（YouTube が新しいタブで開きます）`}</span>
                                </a>
                            )
                            : <div className={cardClass}>{card}</div>}

                        <button
                            type='button'
                            onClick={() => setCollapsed(true)}
                            aria-label='配信カードを小さくする'
                            className='absolute -right-1.5 -top-1.5 flex size-6 items-center justify-center rounded-full bg-ink text-mist shadow ring-2 ring-mist focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold'
                        >
                            <X className='size-3' />
                        </button>
                    </div>
                )}
        </aside>
    )
}

export default LiveWidget
